import { useMemo } from 'react';
import { useFlags } from '@flagsmith/flagsmith/react';
import flagsmith from '@flagsmith/flagsmith';
import { debugLog } from '@/lib/debug';
import { useFeatureFlagsApi } from './api/useFeatureFlagsApi';

function getLocalOverride(flagKey: string): boolean | undefined {
  if (!flagKey) return undefined;
  const envKey = `VITE_FF_${flagKey.toUpperCase()}`;
  const value = import.meta.env[envKey];
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * Hook to access feature flags with hybrid approach
 *
 * Tries FlagsmithProvider first (via useFlags), falls back to backend API if Flagsmith fails.
 * Returns consistent interface regardless of source.
 *
 * @param flagKeys - Array of flag keys to fetch
 * @returns Object with flag values and loading/error states
 *
 * @example
 * const { flags, isLoading } = useFeatureFlags(['flag1', 'flag2']);
 * const flag1Value = flags.flag1 ?? false;
 */
export function useFeatureFlags(flagKeys: string[]): {
  flags: Record<string, boolean>;
  isLoading: boolean;
  isError: boolean;
} {
  // Filter out any undefined/null/empty flag keys
  const validFlagKeys = flagKeys.filter(key => key && typeof key === 'string');

  // Try Flagsmith first (client-side evaluation)
  const flagsmithFlags = useFlags(validFlagKeys);

  // Check Flagsmith instance state for debugging
  const flagsmithState = {
    identity: flagsmith.identity,
    traits: flagsmith.getAllTraits(),
    hasIdentity: !!flagsmith.identity,
    evaluationContext: flagsmith.getContext(),
  };

  debugLog('[Feature Flags] useFlags returned:', {
    flagKeys: validFlagKeys,
    flagsmithFlags,
    flagsmithFlagsKeys: Object.keys(flagsmithFlags),
    flagValues: validFlagKeys.map(key => ({
      key,
      flag: flagsmithFlags[key],
      enabled: flagsmithFlags[key]?.enabled,
      value: flagsmithFlags[key]?.value,
    })),
    flagsmithState,
  });

  // Determine if Flagsmith has values for all requested flags
  const flagsmithHasAllFlags = useMemo(() => {
    const hasAll = validFlagKeys.every(key => {
      const flag = flagsmithFlags[key];
      return flag !== undefined && flag !== null;
    });
    debugLog('[Feature Flags] Flagsmith has all flags?', {
      flagKeys: validFlagKeys,
      hasAll,
      missingFlags: validFlagKeys.filter(
        key => flagsmithFlags[key] === undefined || flagsmithFlags[key] === null
      ),
    });
    return hasAll;
  }, [validFlagKeys, flagsmithFlags]);

  // Only fetch from backend API if Flagsmith doesn't have all flags
  const {
    data: apiFlags,
    isLoading: apiLoading,
    isError: apiError,
  } = useFeatureFlagsApi(validFlagKeys, !flagsmithHasAllFlags);

  // Use Flagsmith if available, otherwise fall back to API
  // Local overrides (VITE_FF_<FLAG_NAME>=true) take precedence
  const flags = useMemo(() => {
    const result: Record<string, boolean> = {};

    for (const key of validFlagKeys) {
      // Check for local override first
      const localOverride = getLocalOverride(key);
      if (localOverride !== undefined) {
        result[key] = localOverride;
        continue;
      }

      // Try Flagsmith
      if (flagsmithHasAllFlags && flagsmithFlags[key]) {
        result[key] = flagsmithFlags[key]?.enabled ?? false;
        continue;
      }

      // Try API
      if (apiFlags && apiFlags[key] !== undefined) {
        result[key] = apiFlags[key];
        continue;
      }

      // Default to false
      result[key] = false;
    }

    return result;
  }, [validFlagKeys, flagsmithFlags, flagsmithHasAllFlags, apiFlags]);

  const isLoading = !flagsmithHasAllFlags && apiLoading;
  const isError = !flagsmithHasAllFlags && apiError;

  return {
    flags,
    isLoading,
    isError,
  };
}
